import { useQueryClient } from '@tanstack/react-query'
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { api, isApiError, onUnauthorized } from '../api/client'
import type { AuthResponse } from '../api/types'
import { dropInviteFromUrl, getInvite } from '../lib/invite'
import { getWebApp, isInTelegram, type TelegramWidgetUser } from '../telegram'
import { AuthContext, type AuthContextValue, type AuthErrorKind, type AuthState } from './context'
import { clearToken, getToken, setToken } from './token'

const LOADING: AuthState = { status: 'loading' }
const SIGNED_OUT: AuthState = { status: 'signed_out' }

function errorKind(err: unknown): AuthErrorKind {
  if (isApiError(err, 0)) return 'network'
  if (isApiError(err, 401)) return err.detail === 'expired' ? 'expired' : err.detail === 'bad_signature' ? 'bad_signature' : 'session'
  return 'unknown'
}

const LOGIN_ERRORS: Record<AuthErrorKind, string> = {
  network: 'Сервер не отвечает. Проверьте соединение и попробуйте ещё раз.',
  expired: 'Данные входа устарели. Попробуйте ещё раз.',
  bad_signature: 'Telegram не подтвердил вход. Попробуйте ещё раз.',
  session: 'Не получилось войти. Попробуйте ещё раз.',
  unknown: 'Не получилось войти. Попробуйте ещё раз.',
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient()
  const [state, setState] = useState<AuthState>(() => (isInTelegram() || getToken() ? LOADING : SIGNED_OUT))

  const finish = useCallback((res: AuthResponse) => {
    setToken(res.token)
    dropInviteFromUrl()
    setState({ status: 'signed_in', user: res.user })
  }, [])

  const noAccess = useCallback(() => setState({ status: 'no_access', hadInvite: getInvite() !== null }), [])

  // Внутри Telegram входим по initData при каждом запуске: так пользователь всегда тот, чей это Telegram.
  const boot = useCallback(async () => {
    const tg = getWebApp()
    if (tg && isInTelegram()) {
      setState(LOADING)
      try {
        finish(await api.authWebApp({ init_data: tg.initData, invite: getInvite() }))
      } catch (err) {
        if (isApiError(err, 403)) noAccess()
        else setState({ status: 'error', kind: errorKind(err) })
      }
      return
    }
    if (!getToken()) {
      setState(SIGNED_OUT)
      return
    }
    setState(LOADING)
    try {
      setState({ status: 'signed_in', user: await api.me() })
    } catch (err) {
      if (isApiError(err, 401)) setState(SIGNED_OUT)
      else if (isApiError(err, 403)) noAccess()
      else setState({ status: 'error', kind: errorKind(err) })
    }
  }, [finish, noAccess])

  const booted = useRef(false)
  useEffect(() => {
    if (booted.current) return
    booted.current = true
    void boot()
  }, [boot])

  // 401 на любом запросе после входа: токен уже стёрт клиентом.
  useEffect(
    () =>
      onUnauthorized(() => {
        qc.clear()
        setState(isInTelegram() ? { status: 'error', kind: 'session' } : SIGNED_OUT)
      }),
    [qc],
  )

  const signIn = useCallback(
    async (call: () => Promise<AuthResponse>): Promise<string | null> => {
      try {
        finish(await call())
        return null
      } catch (err) {
        if (isApiError(err, 403)) {
          noAccess()
          return null
        }
        if (isApiError(err, 404)) return 'Этот способ входа выключен на сервере.'
        return LOGIN_ERRORS[errorKind(err)]
      }
    },
    [finish, noAccess],
  )

  const value = useMemo<AuthContextValue>(
    () => ({
      state,
      signInWithWidget: (u: TelegramWidgetUser) =>
        signIn(() =>
          api.authWidget({
            id: u.id,
            first_name: u.first_name,
            last_name: u.last_name ?? null,
            username: u.username ?? null,
            photo_url: u.photo_url ?? null,
            auth_date: u.auth_date,
            hash: u.hash,
            invite: getInvite(),
          }),
        ),
      signInWithDev: (telegramId: number) => signIn(() => api.authDev({ telegram_id: telegramId })),
      signOut: () => {
        clearToken()
        qc.clear()
        setState(SIGNED_OUT)
      },
      retry: () => void boot(),
    }),
    [state, signIn, boot, qc],
  )

  return <AuthContext value={value}>{children}</AuthContext>
}
